import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, TextField, Button, FormControl, 
  FormLabel, RadioGroup, Radio, FormControlLabel, 
  Select, MenuItem, Checkbox 
} from '@mui/material';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import axios from 'axios';
import { getClients, criarExpedicao } from '../services/api';  // Funções da API
import ConfirmModalMUI from '../warehouse/modals/ConfirmModalMUI';
import FeedbackModalMUI from '../warehouse/modals/FeedbackModalMUI';

const CriarExpedicao = ({ open, onClose, apiBaseURL, onSubmit }) => {
  // Campos do formulário
  const [expeditionDate, setExpeditionDate] = useState(new Date().toISOString().slice(0, 10));
  const [fileFormat, setFileFormat] = useState("PDF");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [volumes, setVolumes] = useState("");
  const [volumeDetails, setVolumeDetails] = useState([]);
  const [notifyClient, setNotifyClient] = useState(false);

  // Estados para modais e feedback
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);

  // Buscar clientes ao montar o componente
  useEffect(() => {
    getClients()
      .then(response => setClients(response.data))
      .catch(error => console.error("Erro ao obter clientes:", error));
  }, []);

  // Quando o número de volumes for alterado, atualiza o estado volumeDetails com valores padrão
  useEffect(() => {
    const numVolumes = parseInt(volumes);
    if (!isNaN(numVolumes) && numVolumes > 0) {
      const details = Array.from({ length: numVolumes }, () => ({
        weight: 0, 
        shipmentType: "Palete Euro",
      }));
      setVolumeDetails(details);
    } else {
      setVolumeDetails([]);
    }
  }, [volumes]);

  // Função para atualizar os detalhes de cada volume
  const handleVolumeDetailChange = (index, field, value) => {
    const updatedDetails = [...volumeDetails];
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    setVolumeDetails(updatedDetails);
  };

  // Filtrar clientes conforme termo de busca
  const filteredClients = clients.filter(client => {
    const search = searchTerm.toLowerCase();
    return (
      (client.nome2 && client.nome2.toLowerCase().includes(search)) ||
      (client.name && client.name.toLowerCase().includes(search))
    );
  });

  // Buscar moradas do cliente selecionado
  const fetchAddresses = async (clientId) => {
    const token = localStorage.getItem("access");
    try {
      const response = await axios.get(
        `${apiBaseURL}/moradas-dachser/?client_id=${clientId}`,
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      setAddresses(response.data);
    } catch (error) {
      console.error("Erro ao buscar moradas:", error);
    }
  };

  useEffect(() => {
    if (selectedClient && selectedClient.id) {
      fetchAddresses(selectedClient.id);
    }
  }, [selectedClient]);

  // Função que valida se todos os campos estão preenchidos
  const validateForm = () => {
    if (!expeditionDate) return "A data da expedição é obrigatória.";
    if (!invoiceNumber) return "O número da fatura é obrigatório.";
    if (!selectedClient) return "Você deve selecionar um cliente.";
    if (!selectedAddress) return "Você deve selecionar uma morada.";
    if (!volumes || parseInt(volumes) <= 0) return "Informe um número válido de volumes/paletes.";
    // Verifica cada volume: o peso deve ser maior que zero e o tipo deve estar preenchido
    for (let i = 0; i < volumeDetails.length; i++) {
      if (!volumeDetails[i].weight || volumeDetails[i].weight <= 0)
        return `Informe um peso válido para o volume ${i + 1}.`;
      if (!volumeDetails[i].shipmentType)
        return `Selecione um tipo para o volume ${i + 1}.`;
    }
    return "";
  };

  // Resetar os campos após a criação
  const resetFields = () => {
    setExpeditionDate(new Date().toISOString().slice(0, 10));
    setFileFormat("PDF");
    setInvoiceNumber("");
    setSelectedClient(null);
    setSearchTerm("");
    setAddresses([]);
    setSelectedAddress("");
    setVolumes("");
    setVolumeDetails([]);
    setNotifyClient(false);
  };

  // Ao clicar em "Criar Expedição", exibe o modal de confirmação
  const handleDialogSubmit = () => {
    setShowConfirmModal(true);
  };

  // Função executada quando o usuário confirma a expedição
  const handleConfirm = async () => {
    // Antes de enviar, vamos validar o formulário
    const error = validateForm();
    if (error) {
      setFeedbackMessage(error);
      setSubmissionComplete(false);
      setShowFeedbackModal(true);
      return;
    }

    setShowConfirmModal(false);
    setIsSubmitting(true);
    setShowFeedbackModal(true);
    const payload = {
      transportDate: expeditionDate,
      fileFormat,
      invoiceNumber,
      clientId: selectedClient ? selectedClient.id : null,
      address: selectedAddress,
      volumes: volumeDetails.length, // quantidade de volumes
      volumeDetails,                 // array com os detalhes de cada volume
      notifyClient,
    };
  
    console.log("Dados da expedição:", payload);
  
    try {
      const response = await criarExpedicao(payload);
      console.log("Expedição criada com sucesso:", response.data);
      setFeedbackMessage("Expedição criada com sucesso!");
      setSubmissionComplete(true);
      resetFields();
      if (onSubmit) onSubmit();
      onClose();  // Notifica o componente pai, se necessário
    } catch (error) {
      console.error("Erro ao criar expedição:", error.response || error);
      // Extrai a mensagem de erro, se existir, ou usa uma mensagem padrão.
      const erroMsg = error.response && error.response.data && error.response.data.error 
                        ? error.response.data.error 
                        : "Erro ao criar expedição.";
      setFeedbackMessage(erroMsg);
      setSubmissionComplete(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmModal(false);
  };

  const handleFeedbackClose = () => {
    setShowFeedbackModal(false);
    setFeedbackMessage("");
    setSubmissionComplete(false);
    setIsSubmitting(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Criar Expedição</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Data da Expedição */}
            <TextField
              label="Data da Expedição"
              type="date"
              fullWidth
              sx={{ mt: 2 }}
              value={expeditionDate}
              onChange={(e) => setExpeditionDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            {/* Formato do ficheiro */}
            <FormControl component="fieldset" sx={{ mt: 2 }} required>
              <FormLabel component="legend">Formato do Ficheiro</FormLabel>
              <RadioGroup row value={fileFormat} onChange={(e) => setFileFormat(e.target.value)}>
                <FormControlLabel value="ZPL" control={<Radio />} label="Etiquetas em ZPL" />
                <FormControlLabel value="PDF" control={<Radio />} label="PDF" />
              </RadioGroup>
            </FormControl>
            {/* Número da Fatura */}
            <TextField
              label="Número da Fatura"
              fullWidth
              sx={{ mt: 2 }}
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              required
            />
            {/* Pesquisa de Clientes */}
            <TextField
              label="Pesquisar Cliente"
              fullWidth
              sx={{ mt: 2 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Box sx={{ mt: 2 }}>
              <DataTable
                value={filteredClients}
                paginator
                rows={5}
                dataKey="id"
                selectionMode="single"
                selection={selectedClient}
                onSelectionChange={(e) => setSelectedClient(e.value)}
                style={{ cursor: 'pointer' }}
              >
                <Column field="nome2" header="Abreviatura" sortable />
                <Column field="name" header="Nome" sortable />
              </DataTable>
            </Box>
            {/* Seleção de Morada */}
            {selectedClient && addresses.length > 0 && (
              <TextField
                select
                label="Selecione a Morada"
                fullWidth
                sx={{ mt: 2 }}
                value={selectedAddress}
                onChange={(e) => setSelectedAddress(e.target.value)}
                required
              >
                {addresses.map((addr) => (
                  <MenuItem key={addr.id} value={addr.defenicao}>
                    {addr.defenicao}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {/* Volumes/Paletes */}
            <TextField
              label="Volumes/Paletes"
              fullWidth
              sx={{ mt: 2 }}
              value={volumes}
              onChange={(e) => setVolumes(e.target.value)}
              required
            />
            {volumeDetails.map((detail, index) => (
              <Box key={index} sx={{ mb: 2, border: '1px solid #ccc', p: 2 }}>
                <TextField
                  label={`Peso Volume ${index + 1}`}
                  type="number"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={detail.weight}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value, 10);
                    handleVolumeDetailChange(index, "weight", isNaN(newValue) ? 0 : newValue);
                  }}
                  required
                />
                <FormControl fullWidth sx={{ mt: 2 }} required>
                  <Select
                    value={detail.shipmentType}
                    onChange={(e) =>
                      handleVolumeDetailChange(index, "shipmentType", e.target.value)
                    }
                  >
                    <MenuItem value="Palete Euro">Palete Euro</MenuItem>
                    <MenuItem value="Volume">Volume</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label={`Altura Volume ${index + 1}`}
                  type="number"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={detail.height || ''}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value, 10);
                    handleVolumeDetailChange(index, "height", isNaN(newValue) ? 0 : newValue);
                  }}
                  required
                />
              </Box>
            ))}
            {/* Checkbox para avisar o cliente 30 min antes */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyClient}
                  onChange={(e) => setNotifyClient(e.target.checked)}
                />
              }
              label="Avisar cliente 30 min antes"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button 
          onClick={handleDialogSubmit} 
          variant="contained" 
          color="primary"
            disabled={isSubmitting}
        >
          {isSubmitting ? "Criando..." : "Criar Expedição"}
        </Button>
        </DialogActions>
      </Dialog>
      <ConfirmModalMUI 
        open={showConfirmModal} 
        message="Tem certeza que deseja criar a expedição?" 
        onConfirm={handleConfirm} 
        onCancel={handleConfirmCancel}
      />
      <FeedbackModalMUI 
        open={showFeedbackModal} 
        isSubmitting={isSubmitting} 
        submissionComplete={submissionComplete} 
        error={!submissionComplete && feedbackMessage !== "Expedição criada com sucesso!" ? feedbackMessage : null} 
        onClose={handleFeedbackClose} 
      />
    </>
  );
};

export default CriarExpedicao;
